import { useSearchParams } from "react-router";
import {
  FORWARDING_ORDERS_DEFAULTS,
  TRANSPORT_ORDERS_DEFAULTS,
} from "../helpers/defaultUrl";
import { formatDateRange, parseDateRange } from "../helpers/date";
import { useCallback, useEffect, useState } from "react";

const setParamIfNotDefault = (
  params: URLSearchParams,
  key: string,
  value: string | number | boolean,
  defaultValue: string | number | boolean | null,
) => {
  if (value !== defaultValue) {
    params.set(key, String(value));
  } else {
    params.delete(key);
  }
};

export function useDataGridQuery(defaults: {
  page: number;
  limit: number;
  sort: string;
  order: number;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract pagination and sorting parameters
  const page = Number(searchParams.get("page") ?? defaults.page);
  const limit = Number(searchParams.get("limit") ?? defaults.limit);
  const sort = searchParams.get("sort") ?? defaults.sort;
  const order = Number(searchParams.get("order") ?? defaults.order);

  // Handlers for updating query parameters
  const onPageChange = useCallback(
    (newPage: number, newLimit: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        setParamIfNotDefault(params, "page", newPage, defaults.page);
        setParamIfNotDefault(params, "limit", newLimit, defaults.limit);
        return params;
      });
    },
    [defaults.page, defaults.limit, setSearchParams],
  );

  const onSortChange = useCallback(
    (newSort: string, newOrder: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        setParamIfNotDefault(params, "sort", newSort, defaults.sort);
        setParamIfNotDefault(params, "order", newOrder, defaults.order);
        return params;
      });
    },
    [defaults.order, defaults.sort, setSearchParams],
  );

  return {
    page,
    limit,
    sort,
    order,
    onPageChange,
    onSortChange,
  };
}

function useSearchQuery(defaultValue: string = "") {
  const [searchParams, setSearchParams] = useSearchParams();
  const [globalFilterValue, setGlobalFilterValue] = useState(
    searchParams.get("search") || defaultValue,
  );

  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (globalFilterValue) {
        params.set("search", globalFilterValue);
      } else {
        params.delete("search");
      }
      return params;
    });
  }, [globalFilterValue, setSearchParams]);

  return { globalFilterValue, setGlobalFilterValue };
}

export function useUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const { globalFilterValue, setGlobalFilterValue } = useSearchQuery();

  // All other params that aren't pagination/sorting are filters
  const filters = Object.fromEntries(
    Array.from(searchParams.entries()).filter(
      ([key]) =>
        ![
          "page",
          "limit",
          "sort",
          "order",
          "showCompletedRows",
          "search",
          "loadingDate",
          "unloadingDate",
          "date",
        ].includes(key),
    ),
  );

  // Extract showCompletedRows from searchParams
  const showCompletedRows =
    searchParams.get("showCompletedRows") !== null
      ? searchParams.get("showCompletedRows") === "true"
      : TRANSPORT_ORDERS_DEFAULTS.showCompletedRows;

  const setShowCompletedRows = (value: boolean) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      if (value === TRANSPORT_ORDERS_DEFAULTS.showCompletedRows) {
        // Remove if it matches the default
        params.delete("showCompletedRows");
      } else {
        params.set("showCompletedRows", String(value));
      }

      return params;
    });
  };

  const [loadingFrom, loadingTo] = parseDateRange(
    searchParams.get("loadingDate") ?? TRANSPORT_ORDERS_DEFAULTS.loadingDate,
  );
  const [unloadingFrom, unloadingTo] = parseDateRange(
    searchParams.get("unloadingDate") ??
      TRANSPORT_ORDERS_DEFAULTS.unloadingDate,
  );
  const [dateFrom, dateTo] = parseDateRange(
    searchParams.get("date") ?? FORWARDING_ORDERS_DEFAULTS.date,
  );

  const setDateRange = (
    field: string,
    from: Date | undefined,
    to: Date | undefined,
  ) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);

      const formattedDateRange = formatDateRange(from, to);
      if (formattedDateRange) {
        params.set(field, formattedDateRange);
      } else {
        params.delete(field);
      }

      return params;
    });
  };

  return {
    // Current values
    loadingFrom,
    loadingTo,
    unloadingFrom,
    unloadingTo,
    dateFrom,
    dateTo,
    setDateRange,
    showCompletedRows,
    setShowCompletedRows,
    filters,
    globalFilterValue,
    setGlobalFilterValue,
    searchParams,
    setSearchParams,
  };
}
